import React, { useCallback, useEffect, useState } from 'react';
import {
  Table, Button, Modal, Form, Input, Switch, Space, Tag, Typography,
  message, Popconfirm, Row, Col, Statistic, Card, Drawer, Empty,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, LinkOutlined,
  BarChartOutlined, GlobalOutlined, ReloadOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/pt-br';
import api from '../services/api';

dayjs.extend(relativeTime);
dayjs.locale('pt-br');

const { Title, Text, Paragraph } = Typography;

const AdminLandingPages = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState({ open: false, editing: null });
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const [drawer, setDrawer] = useState({ open: false, page: null, ctas: [] });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/landing-pages');
      setRows(r.data?.landing_pages || []);
    } catch (err) {
      message.error(err?.response?.data?.error || 'Erro ao listar');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setModal({ open: true, editing: null });
    form.resetFields();
    form.setFieldsValue({ active: true });
  };

  const openEdit = (row) => {
    setModal({ open: true, editing: row });
    form.setFieldsValue({
      path: row.path,
      title: row.title,
      description: row.description,
      active: row.active,
    });
  };

  const submit = async () => {
    const vals = await form.validateFields();
    setSaving(true);
    try {
      const payload = { ...vals };
      if (modal.editing) payload.id = modal.editing.id;
      await api.post('/admin/landing-pages', payload);
      message.success(modal.editing ? 'Atualizado' : 'Cadastrado');
      setModal({ open: false, editing: null });
      load();
    } catch (err) {
      message.error(err?.response?.data?.error || err.message);
    }
    setSaving(false);
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/landing-pages/${id}`);
      message.success('Removido');
      load();
    } catch (err) { message.error(err?.response?.data?.error || 'Erro'); }
  };

  const openBreakdown = async (row) => {
    setDrawer({ open: true, page: row, ctas: [] });
    try {
      const r = await api.get(`/admin/landing-pages/${row.id}/cta-breakdown`);
      setDrawer(d => ({ ...d, ctas: r.data?.ctas || [] }));
    } catch (err) {
      message.error(err?.response?.data?.error || 'Erro no breakdown');
    }
  };

  const totalViews7d = rows.reduce((s, r) => s + (r.views_7d || 0), 0);
  const totalClicks7d = rows.reduce((s, r) => s + (r.clicks_7d || 0), 0);
  const avgCtr = totalViews7d > 0 ? (totalClicks7d / totalViews7d * 100) : 0;

  const columns = [
    {
      title: 'Landing page', dataIndex: 'title', width: 320,
      render: (v, r) => (
        <Space direction="vertical" size={0}>
          <Space>
            <Text strong>{v}</Text>
            {!r.active && <Tag>INATIVA</Tag>}
          </Space>
          <a href={r.path} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12 }}>
            <LinkOutlined /> {r.path}
          </a>
          {r.description && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              {r.description.slice(0, 90)}{r.description.length > 90 ? '…' : ''}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Publicada em', dataIndex: 'created_at', width: 140,
      render: v => <Text type="secondary" style={{ fontSize: 12 }}>{dayjs(v).format('DD/MM/YYYY')}</Text>,
    },
    {
      title: 'Views (7d)', dataIndex: 'views_7d', width: 100, align: 'right',
      render: v => <Text strong>{v}</Text>,
    },
    {
      title: 'Cliques CTA (7d)', dataIndex: 'clicks_7d', width: 130, align: 'right',
      render: v => <Text strong style={{ color: '#52c41a' }}>{v}</Text>,
    },
    {
      title: 'CTR (7d)', width: 90, align: 'right',
      render: (_, r) => {
        const v = r.views_7d || 0;
        const c = r.clicks_7d || 0;
        return <Text>{v > 0 ? ((c / v) * 100).toFixed(2) : '0.00'}%</Text>;
      },
    },
    {
      title: 'Total lifetime', width: 140, align: 'right',
      render: (_, r) => (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {r.views_total} views · {r.clicks_total} clicks
        </Text>
      ),
    },
    {
      title: 'Ações', width: 180, align: 'center',
      render: (_, r) => (
        <Space>
          <Button size="small" icon={<BarChartOutlined />} onClick={() => openBreakdown(r)}>CTAs</Button>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
          <Popconfirm title="Remover LP e todo o tracking dela?" onConfirm={() => remove(r.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={2} style={{ margin: 0 }}>
            <GlobalOutlined /> Landing Pages
          </Title>
          <Text type="secondary">
            LPs publicadas (release notes, anúncios, campanhas). Tracking: views + cliques em CTA.
          </Text>
        </Col>
        <Col>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Atualizar</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={openNew}>Cadastrar LP</Button>
          </Space>
        </Col>
      </Row>

      <Row gutter={12} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}><Card size="small"><Statistic title="LPs cadastradas" value={rows.length} /></Card></Col>
        <Col xs={12} md={6}><Card size="small"><Statistic title="Views (7d)" value={totalViews7d} /></Card></Col>
        <Col xs={12} md={6}><Card size="small"><Statistic title="Cliques CTA (7d)" value={totalClicks7d} valueStyle={{ color: '#52c41a' }} /></Card></Col>
        <Col xs={12} md={6}><Card size="small"><Statistic title="CTR médio (7d)" value={avgCtr.toFixed(2)} suffix="%" /></Card></Col>
      </Row>

      <Table
        columns={columns}
        dataSource={rows}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 20 }}
        scroll={{ x: 1200 }}
      />

      <Modal
        title={modal.editing ? `Editar LP #${modal.editing.id}` : 'Cadastrar landing page'}
        open={modal.open}
        onCancel={() => setModal({ open: false, editing: null })}
        onOk={submit}
        confirmLoading={saving}
        okText="Salvar"
        cancelText="Cancelar"
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item name="path" label="Path (URL relativa)" rules={[{ required: true }]}>
            <Input placeholder="/como-funciona-ranking" />
          </Form.Item>
          <Form.Item name="title" label="Título" rules={[{ required: true, max: 200 }]}>
            <Input placeholder="Novidade: Ranking do time" />
          </Form.Item>
          <Form.Item name="description" label="Descrição (opcional)">
            <Input.TextArea rows={3} maxLength={500} showCount />
          </Form.Item>
          <Form.Item name="active" label="Ativa" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      <Drawer
        title={<span><BarChartOutlined /> Cliques por CTA — {drawer.page?.title}</span>}
        open={drawer.open}
        onClose={() => setDrawer({ open: false, page: null, ctas: [] })}
        width={480}
      >
        <Text type="secondary">Últimos 7 dias. Se um CTA não tem label instrumentado, aparece como "(sem label)".</Text>
        <div style={{ marginTop: 16 }}>
          {drawer.ctas.length === 0 ? (
            <Empty description="Nenhum clique nos últimos 7 dias" />
          ) : (
            <Table
              size="small"
              pagination={false}
              dataSource={drawer.ctas}
              rowKey="cta_label"
              columns={[
                { title: 'CTA', dataIndex: 'cta_label' },
                { title: 'Cliques', dataIndex: 'clicks', align: 'right' },
              ]}
            />
          )}
        </div>
      </Drawer>
    </div>
  );
};

export default AdminLandingPages;
